import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSave,
  FiCheckCircle,
  FiAlertCircle,
  FiPhone,
  FiHome,
  FiEdit,
  FiCopy,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiClock,
  FiShield,
  FiX,
  FiUserPlus,
  FiSend,
  FiMail,
  FiUsers,
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiTrendingUp,
  FiDollarSign,
  FiSettings,
  FiLogOut,
} from "react-icons/fi";
import { FaCrown, FaDeaf, FaTrash } from "react-icons/fa";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import HeaderActions from "../components/HeaderActions";
import Sidebar from "../components/Sidebar";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.18 }}
      className={`fixed top-5 right-5 z-50 w-80 px-4 py-3.5 rounded-lg flex items-start gap-3 border shadow-sm bg-white ${
        type === "success" ? "border-l-4 border-l-[#1F9D6C]" : "border-l-4 border-l-[#C62828]"
      } border-t border-r border-b border-[#E4DCE6]`}
      role="alert"
      aria-live="polite"
    >
      {type === "success" ? (
        <FiCheckCircle size={18} className="shrink-0 mt-0.5 text-[#1F9D6C]" />
      ) : (
        <FiAlertCircle size={18} className="shrink-0 mt-0.5 text-[#C62828]" />
      )}
      <span className="text-sm font-medium text-[#3D2542] flex-1">{message}</span>
      <button
        onClick={onClose}
        className="p-1 rounded hover:bg-[#F0E6F5] transition-colors text-[#7A6B7E]"
        aria-label="Close notification"
      >
        <FiX size={14} />
      </button>
    </motion.div>
  );
};

const LoadingSpinner = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "w-4 h-4 border-2",
    md: "w-10 h-10 border-[3px]",
    lg: "w-14 h-14 border-[3px]",
  };
  return (
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
      className={`${sizeClasses[size]} border-[#B89D4F] border-t-transparent rounded-full`}
    />
  );
};

const InputField = ({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  placeholder,
  error,
  success,
  hint,
  type = "text",
  className = "",
  ...props
}) => (
  <div>
    <label htmlFor={id} className="text-xs font-semibold text-[#5B3A60] uppercase tracking-wide flex items-center gap-1.5 mb-2">
      {Icon && <Icon size={13} className="text-[#B89D4F]" aria-hidden="true" />}
      {label}
    </label>
    <div className="relative">
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className={`w-full px-3.5 py-2.5 rounded-md border outline-none transition-colors duration-150 focus:ring-2 text-sm ${
          error
            ? "border-[#C62828] focus:border-[#C62828] focus:ring-[#C62828]/15 bg-[#FDEEEE]"
            : success
            ? "border-[#1F9D6C] focus:border-[#1F9D6C] focus:ring-[#1F9D6C]/15 bg-[#F5FBF8]"
            : "border-[#E4DCE6] focus:border-[#5B3A60] focus:ring-[#5B3A60]/10 hover:border-[#C9BDCC] bg-white"
        } ${className}`}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : success ? `${id}-success` : hint ? `${id}-hint` : undefined}
        {...props}
      />
      {success && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1F9D6C]">
          <FiCheckCircle size={16} aria-hidden="true" />
        </div>
      )}
    </div>
    <AnimatePresence mode="wait">
      {error && (
        <motion.p
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          id={`${id}-error`}
          className="text-xs text-[#C62828] mt-1.5 flex items-center gap-1.5"
          role="alert"
        >
          <FiAlertCircle size={12} aria-hidden="true" /> {error}
        </motion.p>
      )}
      {success && !error && (
        <motion.p
          key="success"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          id={`${id}-success`}
          className="text-xs text-[#1F9D6C] mt-1.5 flex items-center gap-1.5"
        >
          <FiCheckCircle size={12} aria-hidden="true" /> {success}
        </motion.p>
      )}
      {hint && !error && !success && (
        <motion.p
          key="hint"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          id={`${id}-hint`}
          className="text-xs text-[#7A6B7E] mt-1.5 flex items-center gap-1.5"
        >
          <FiAlertCircle size={12} aria-hidden="true" /> {hint}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const ActionButton = ({ children, onClick, variant = "primary", disabled, loading, icon: Icon, className = "", ...props }) => {
  const variants = {
    primary: "bg-[#5B3A60] text-white hover:bg-[#4A2E50] border border-[#5B3A60]",
    secondary: "bg-white text-[#5B3A60] border border-[#E4DCE6] hover:border-[#5B3A60] hover:bg-[#FAF8FB]",
    danger: "bg-white text-[#C62828] border border-[#F0C7C7] hover:bg-[#FDEEEE]",
    gold: "bg-[#B89D4F] text-white hover:bg-[#A88D3F] border border-[#B89D4F]",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-colors duration-150 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/40 disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
      {...props}
    >
      {loading ? <LoadingSpinner size="sm" /> : Icon && <Icon size={15} aria-hidden="true" />}
      {loading ? "Processing…" : children}
    </button>
  );
};

const StatCard = ({ label, value, icon: Icon, tone = "plum" }) => {
  const tones = {
    plum: "bg-[#F0E6F5] text-[#5B3A60]",
    gold: "bg-[#FBF3DC] text-[#A88D3F]",
    success: "bg-[#E8F7F0] text-[#1F9D6C]",
  };
  return (
    <div className="flex items-center gap-4 p-5 bg-white rounded-lg border border-[#E4DCE6]">
      <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-[#7A6B7E] uppercase tracking-wide">{label}</p>
        <p className="text-lg font-bold text-[#3D2542] truncate">{value}</p>
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, action }) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4DCE6]">
    <div className="flex items-center gap-2.5">
      <Icon className="text-[#B89D4F]" size={17} />
      <h2 className="text-[15px] font-bold text-[#3D2542]">{title}</h2>
    </div>
    {action}
  </div>
);

// ==========================================
// INVITE MEMBER MODAL
// ==========================================
const InviteMemberModal = ({ isOpen, onClose, tenantId, onInviteSuccess }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const authToken = localStorage.getItem("token");

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${getApiBase()}/team/invite?tenant_id=${tenantId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Failed to send invitation");
      }

      setSuccess(data.message || "Invitation sent successfully!");
      setEmail("");

      if (onInviteSuccess) onInviteSuccess();

      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 2000);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#3D2542]/50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.15 }}
          className="w-full max-w-md bg-white rounded-lg border border-[#E4DCE6] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-[#E4DCE6] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#F0E6F5] flex items-center justify-center">
                <FiUserPlus className="text-[#5B3A60] text-base" />
              </div>
              <h2 className="text-[15px] font-bold text-[#3D2542]">Invite Team Member</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[#F0E6F5] transition-colors text-[#7A6B7E]" aria-label="Close modal">
              <FiX size={18} />
            </button>
          </div>

          <form onSubmit={handleInvite} className="p-6 space-y-5">
            <p className="text-sm text-[#7A6B7E]">
              Enter the email address of the person you want to invite. They will receive an email with instructions to join your workspace.
            </p>

            <InputField
              id="inviteEmail"
              label="Email Address"
              icon={FiMail}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              placeholder="colleague@example.com"
              error={error}
              autoFocus
            />

            {success && (
              <div className="p-3 bg-[#E8F7F0] border border-[#BFE8D5] rounded-md text-sm text-[#1F9D6C] flex items-center gap-2">
                <FiCheckCircle size={16} />
                {success}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <ActionButton type="button" onClick={onClose} variant="secondary" icon={FiX}>
                Cancel
              </ActionButton>
              <ActionButton type="submit" variant="primary" loading={loading} icon={FiSend} disabled={!email || !!success}>
                Send Invitation
              </ActionButton>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================
export default function TenantSettings() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  
  // Track if the CURRENT logged-in user is an admin
  const [isAdmin, setIsAdmin] = useState(false);

  const [currentUserName, setCurrentUserName] = useState("");

  const companyName = useMemo(() => localStorage.getItem("company_name") || "Workspace", []);

  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    countryCode: "",
    mobileNumber: "",
    apiKey: "",
  });

  const [initialData, setInitialData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState(null);

  const sidebarItems = useMemo(
    () => [
      { title: "Dashboard", path: `/dashboard/${tenantId}`, icon: <FiGrid /> },
      { title: "Analytics", path: `/analytics/${tenantId}`, icon: <FiTrendingUp /> },
      { title: "Billing", path: `/billing-history/${tenantId}`, icon: <FiDollarSign /> },
      { title: "Settings", path: `/tenant-settings/${tenantId}`, icon: <FiSettings /> },
    ],
    [tenantId]
  );

  useEffect(() => {
    fetchSettings();
    fetchTeamMembers();
  }, [tenantId]);

  useEffect(() => {
    if (initialData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(initialData);
      setHasChanges(changed);
    }
  }, [formData, initialData]);

  useEffect(() => {
    return () => {
      if (validationTimeout) clearTimeout(validationTimeout);
    };
  }, [validationTimeout]);

  const fetchSettings = async () => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(`${getApiBase()}/tenant/${tenantId}/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error();
    const data = await res.json();

    localStorage.setItem("fullname", data.fullname || "");
    localStorage.setItem("company_name", data.company_name || "");
    // NEW: persist identity so role checks work anywhere in the app
    localStorage.setItem("email", data.email || "");
    localStorage.setItem("user_id", data.user_id || "");

    setCurrentUserName(data.fullname || "User");
    setIsAdmin(data.role === "admin"); // <-- set directly from the source of truth

    let countryCode = "";
    let mobileNumber = "";

    if (data.primary_phone) {
      const parsed = parsePhoneNumberFromString(data.primary_phone);
      if (parsed) {
        countryCode = "+" + parsed.countryCallingCode;
        mobileNumber = parsed.nationalNumber;
      }
    }

    const mockKey = data.api_key || `sk_live_${Math.random().toString(36).substr(2, 16)}...`;

    const settings = {
      fullName: data.fullname || "",
      companyName: data.company_name || "",
      countryCode,
      mobileNumber,
      apiKey: mockKey,
    };

    setFormData(settings);
    setInitialData(settings);

    if (countryCode && mobileNumber) {
      validatePhone(countryCode, mobileNumber);
    } else {
      setPhoneValid(false);
    }
  } catch (error) {
    console.error(error);
    setToast({ type: "error", message: "Failed to load settings." });
  } finally {
    setIsLoading(false);
  }
};

  const fetchTeamMembers = async () => {
  setIsLoadingMembers(true);
  try {
    const res = await fetch(`${getApiBase()}/team/members?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error();
    const data = await res.json();
    setTeamMembers(data.members || []);
    // isAdmin is already set from fetchSettings — no need to re-derive it here
  } catch (error) {
    console.error("Failed to fetch team members:", error);
  } finally {
    setIsLoadingMembers(false);
  }
};

  const validatePhone = useCallback((code, number) => {
    if (!code || !number || number.length < 6) {
      setPhoneValid(false);
      return false;
    }

    const cleanCode = code.startsWith("+") ? code : "+" + code;
    const fullNumber = cleanCode + number;
    const parsed = parsePhoneNumberFromString(fullNumber);

    if (!parsed) {
      setPhoneValid(false);
      return false;
    }

    const valid = parsed.isValid();
    setPhoneValid(valid);
    return valid;
  }, []);

  const handleDeleteMember = async (userId, fullName) => {
    const confirmed = window.confirm(`Are you sure you want to delete ${fullName || "this member"}?`);

    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${getApiBase()}/team/member/${userId}?tenant_id=${tenantId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Failed to delete member");
      }

      setToast({
        type: "success",
        message: data.message,
      });

      fetchTeamMembers();
      fetchSettings();
    } catch (err) {
      setToast({
        type: "error",
        message: err.message,
      });
    }
  };

  const handleRoleChange = async (userId, newRole) => {
  // Keep a snapshot so we can roll back on failure
  const previousMembers = teamMembers;

  // Optimistically update local state right away
  setTeamMembers((prev) =>
    prev.map((m) => (m.user_id === userId ? { ...m, role: newRole } : m))
  );

  try {
    const res = await fetch(
      `${getApiBase()}/team/member/${userId}/role?tenant_id=${tenantId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ role: newRole }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.detail || "Failed to update role");
    }

    setToast({ msg: `Role updated to ${newRole}` });
  } catch (err) {
    console.error("Role update failed:", err);
    // Roll back to the previous state on failure
    setTeamMembers(previousMembers);
    setToast({ msg: err.message || "Failed to update role", type: "error" });
  } finally {
    setTimeout(() => setToast(null), 3000);
  }
};

  const debouncedValidatePhone = useCallback(
    (code, number) => {
      if (validationTimeout) clearTimeout(validationTimeout);
      const timeout = setTimeout(() => {
        validatePhone(code, number);
      }, 300);
      setValidationTimeout(timeout);
    },
    [validatePhone, validationTimeout]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }
    if (!phoneValid) {
      newErrors.phone = "Valid mobile number with country code is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.companyName, phoneValid]);

  const handleSave = async () => {
    if (!validateForm()) {
      setToast({ type: "error", message: "Please fix validation errors." });
      return;
    }
    setIsSaving(true);

    const cleanCode = formData.countryCode.startsWith("+") ? formData.countryCode : "+" + formData.countryCode;
    const fullNumber = cleanCode + formData.mobileNumber;
    const parsed = parsePhoneNumberFromString(fullNumber);
    const normalizedPhone = parsed ? parsed.format("E.164") : fullNumber;

    try {
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullname: formData.fullName,
          company_name: formData.companyName,
          phone: normalizedPhone,
        }),
      });
      if (!res.ok) throw new Error();

      localStorage.setItem("company_name", formData.companyName);
      localStorage.setItem("fullname", formData.fullName);
      setCurrentUserName(formData.fullName);

      setInitialData({ ...formData });
      setToast({ type: "success", message: "Settings updated successfully!" });
    } catch {
      setToast({ type: "error", message: "Failed to save settings." });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({ ...initialData });
    setErrors({});
    setToast({ type: "success", message: "Changes discarded." });
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm("Are you sure? This will invalidate your current API key immediately.")) return;
    setIsRegenerating(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    const newKey = `sk_live_${Math.random().toString(36).substr(2, 24)}`;
    setFormData((prev) => ({ ...prev, apiKey: newKey }));
    setToast({ type: "success", message: "API Key regenerated successfully." });
    setIsRegenerating(false);
  };

  const handleChange = useCallback(
    (field, value) => {
      let newFormData = { ...formData, [field]: value };

      if (field === "mobileNumber") {
        const detectedNumber = value.replace(/\D/g, "");
        newFormData.mobileNumber = detectedNumber;
        debouncedValidatePhone(formData.countryCode, detectedNumber);
      }

      if (field === "countryCode") {
        let code = value;
        if (!code.startsWith("+") && code.length > 0 && !isNaN(code)) {
          code = "+" + code;
        }
        newFormData.countryCode = code;
        debouncedValidatePhone(code, formData.mobileNumber);
      }

      setFormData(newFormData);
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: null }));
      }
    },
    [formData, errors, debouncedValidatePhone]
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast({ type: "success", message: "Copied to clipboard!" });
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8FB] font-['Poppins']">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-[#5B3A60] font-medium text-sm">Loading settings…</p>
        </div>
      </div>
    );
  }

  const activeMemberCount = teamMembers.filter((m) => m.is_active).length;

  return (
    <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins']">
      <AnimatePresence>{toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}</AnimatePresence>

      <InviteMemberModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        tenantId={tenantId}
        onInviteSuccess={() => {
          fetchTeamMembers();
        }}
      />

      {/* FLAT SIDEBAR */}
      <aside
        className="fixed top-0 left-0 h-full bg-[#4A2E50] border-r border-black/10 transition-all duration-200 z-40"
        style={{ width: sidebarOpen ? 240 : 72 }}
        aria-label="Main navigation"
      >
        <div className="flex items-center px-4 h-16 border-b border-white/10">
          <div className="w-8 h-8 rounded-md bg-[#B89D4F] flex items-center justify-center shrink-0" aria-hidden="true">
            <span className="text-white text-sm font-bold">{companyName ? companyName[0].toUpperCase() : "D"}</span>
          </div>
          {sidebarOpen && (
            <div className="ml-3 flex flex-col justify-center overflow-hidden">
              <span className="text-white font-semibold text-sm truncate">{companyName || "Workspace"}</span>
              <span className="text-[#B9A6BC] text-[10px] uppercase tracking-wider">Workspace</span>
            </div>
          )}
        </div>

        <nav className="mt-4 space-y-0.5" role="navigation">
          {sidebarItems.map((item) => {
            const active = location.pathname.startsWith(item.path);
            return (
              <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
/>            );
          })}
        </nav>

        <div className="absolute bottom-4 left-0 right-0 px-3">
          <button
            onClick={() => {
              localStorage.clear();
              navigate("/");
            }}
            onKeyDown={(e) =>
              handleKeyDown(e, () => {
                localStorage.clear();
                navigate("/");
              })
            }
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 hover:bg-white/[0.06] text-white text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50"
            aria-label="Logout"
          >
            <FiLogOut size={15} />
            {sidebarOpen && "Logout"}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen transition-all duration-200" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
        {/* FLAT TOP BAR */}
        <div className="h-16 px-8 border-b border-[#E4DCE6] bg-white flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              onKeyDown={(e) => handleKeyDown(e, () => setSidebarOpen(!sidebarOpen))}
              className="p-2 rounded-md hover:bg-[#F0E6F5] transition-colors focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/50"
              aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? <FiChevronLeft className="text-[#7A6B7E]" size={18} /> : <FiChevronRight className="text-[#7A6B7E]" size={18} />}
            </button>

            <nav aria-label="Breadcrumb">
              <div className="flex items-center gap-1.5 text-sm">
                <span className="font-medium text-[#7A6B7E]">{companyName || "Workspace"}</span>
                <span className="text-[#C9BDCC]">/</span>
                <span className="font-semibold text-[#3D2542]">Settings</span>
              </div>
            </nav>
          </div>

          <HeaderActions tenantId={tenantId} fullName={currentUserName} />
        </div>

        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="mb-7">
            <h1 className="text-xl font-bold text-[#3D2542]">Workspace settings</h1>
            <p className="text-sm text-[#7A6B7E] mt-1">Update your company information, contact details, and team access.</p>
          </div>

          {/* STAT ROW - flat grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard label="Company" value={formData.companyName || "Not set"} icon={FiHome} tone="plum" />
            <StatCard label="Phone status" value={phoneValid ? "Verified" : "Incomplete"} icon={FiPhone} tone={phoneValid ? "success" : "gold"} />
            <StatCard label="Team members" value={`${teamMembers.length} total · ${activeMemberCount} active`} icon={FiUsers} tone="gold" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            {/* LEFT COLUMN: Company Info (Span 8) */}
            <div className="lg:col-span-8 space-y-6">
              <section className="bg-white rounded-lg border border-[#E4DCE6]">
                <SectionHeader icon={FiHome} title="Company Information" />
                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <InputField
                    id="fullName"
                    label="Full Name"
                    icon={FiEdit}
                    value={formData.fullName}
                    onChange={(e) => handleChange("fullName", e.target.value)}
                    placeholder="Enter your full name"
                    error={errors.fullName}
                  />
                  <InputField
                    id="companyName"
                    label="Company Name"
                    icon={FiEdit}
                    value={formData.companyName}
                    onChange={(e) => handleChange("companyName", e.target.value)}
                    placeholder="Enter your company name"
                    error={errors.companyName}
                  />

                  <div className="sm:col-span-2">
                    <label htmlFor="phone" className="text-xs font-semibold text-[#5B3A60] uppercase tracking-wide flex items-center gap-1.5 mb-2">
                      <FiPhone size={13} className="text-[#B89D4F]" aria-hidden="true" /> Primary Phone
                    </label>
                    <div className="flex gap-3">
                      <input
                        id="countryCode"
                        type="text"
                        value={formData.countryCode}
                        onChange={(e) => handleChange("countryCode", e.target.value)}
                        className="w-24 px-3 py-2.5 rounded-md border border-[#E4DCE6] focus:border-[#5B3A60] outline-none text-center text-sm font-medium transition-colors focus:ring-2 focus:ring-[#5B3A60]/10 hover:border-[#C9BDCC]"
                        placeholder="+1"
                        aria-label="Country code"
                      />
                      <div className="flex-1 relative">
                        <FiPhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C9BDCC]" aria-hidden="true" size={15} />
                        <input
                          id="mobileNumber"
                          type="tel"
                          value={formData.mobileNumber}
                          onChange={(e) => handleChange("mobileNumber", e.target.value)}
                          className={`w-full pl-10 pr-4 py-2.5 rounded-md border outline-none text-sm transition-colors focus:ring-2 ${
                            !phoneValid && formData.mobileNumber.length > 0
                              ? "border-[#C62828] focus:border-[#C62828] focus:ring-[#C62828]/15 bg-[#FDEEEE]"
                              : "border-[#E4DCE6] focus:border-[#5B3A60] focus:ring-[#5B3A60]/10 hover:border-[#C9BDCC]"
                          }`}
                          placeholder="Enter number"
                          aria-invalid={!phoneValid && formData.mobileNumber.length > 0}
                        />
                        {phoneValid && formData.mobileNumber.length > 0 && (
                          <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#1F9D6C]">
                            <FiCheckCircle size={16} aria-hidden="true" />
                          </div>
                        )}
                      </div>
                    </div>
                    <AnimatePresence mode="wait">
                      {!phoneValid && formData.mobileNumber.length > 0 && (
                        <motion.p
                          key="phone-error"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-xs text-[#C62828] mt-1.5 flex items-center gap-1.5"
                          role="alert"
                        >
                          <FiAlertCircle size={12} aria-hidden="true" /> Invalid format. Ensure country code is correct.
                        </motion.p>
                      )}
                      {phoneValid && formData.mobileNumber.length > 0 && (
                        <motion.p
                          key="phone-success"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-xs text-[#1F9D6C] mt-1.5 flex items-center gap-1.5"
                        >
                          <FiCheckCircle size={12} aria-hidden="true" /> Valid number
                        </motion.p>
                      )}
                      {formData.mobileNumber.length === 0 && (
                        <motion.p key="phone-hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-[#7A6B7E] mt-1.5 flex items-center gap-1.5">
                          <FiAlertCircle size={12} aria-hidden="true" /> Please enter your country code and phone number.
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </section>
              
              {/* Save/Discard Buttons moved here if you want them under Company Info, 
                  or keep them at the very bottom. Usually better under the form they affect. */}
               <AnimatePresence>
                {hasChanges && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="flex justify-end gap-3 p-4 bg-white rounded-lg border border-[#E4DCE6]"
                  >
                    <ActionButton onClick={handleReset} variant="secondary" icon={FiX}>
                      Discard
                    </ActionButton>
                    <ActionButton onClick={handleSave} variant="primary" loading={isSaving} icon={FiSave}>
                      Save Changes
                    </ActionButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* RIGHT COLUMN: Workspace Info (Span 4) */}
            <div className="lg:col-span-4 space-y-6">
              <section className="bg-white rounded-lg border border-[#E4DCE6]">
                <SectionHeader icon={FiHome} title="Workspace Info" />
                <div className="p-6 space-y-4 text-sm">
                  <div className="flex justify-between items-center pb-3 border-b border-[#E4DCE6]">
                    <span className="text-[#7A6B7E]">Company</span>
                    <span className="font-semibold text-[#3D2542] text-right">{formData.companyName || "Not set"}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-[#E4DCE6]">
                    <span className="text-[#7A6B7E]">Team size</span>
                    <span className="font-semibold text-[#3D2542]">{teamMembers.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#7A6B7E] flex items-center gap-1.5">
                      <FiClock size={13} /> Last Updated
                    </span>
                    <span className="text-[#3D2542] font-medium">
                      {new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                    </span>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {/* FULL WIDTH SECTION: Team & Access */}
          <section className="bg-white rounded-lg border border-[#E4DCE6] shadow-sm overflow-hidden">
            <SectionHeader
              icon={FiUsers}
              title="Team & Access"
              description="Manage who has access to your workspace and their permission levels."
              action={
                <ActionButton onClick={() => setIsInviteModalOpen(true)} variant="gold" icon={FiUserPlus}>
                  Invite Member
                </ActionButton>
              }
            />
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#FAF8FB] border-b border-[#E4DCE6]">
                    <th className="px-8 py-4 text-xs font-bold text-[#7A6B7E] uppercase tracking-wider">Member</th>
                    <th className="px-8 py-4 text-xs font-bold text-[#7A6B7E] uppercase tracking-wider">Role</th>
                    <th className="px-8 py-4 text-xs font-bold text-[#7A6B7E] uppercase tracking-wider">Status</th>
                    <th className="px-8 py-4 text-xs font-bold text-[#7A6B7E] uppercase tracking-wider">Joined</th>
                    <th className="px-8 py-4 text-xs font-bold text-[#7A6B7E] uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E4DCE6]">
                  {isLoadingMembers ? (
                    <tr><td colSpan="5" className="py-12 text-center"><LoadingSpinner /></td></tr>
                  ) : teamMembers.length === 0 ? (
                    <tr><td colSpan="5" className="py-12 text-center text-[#7A6B7E]">No members found</td></tr>
                  ) : (
                    teamMembers.map((member) => {
                      const currentUserId = localStorage.getItem("user_id");
                      const isCurrentUser = member.user_id === currentUserId;
                      const canEditRole = isAdmin && !isCurrentUser && member.is_active;

                      return (
                        <tr key={member.user_id} className="hover:bg-[#FAF8FB] transition-colors group">
                          <td className="px-8 py-5 whitespace-nowrap">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-[#F0E6F5] flex items-center justify-center text-[#5B3A60] font-bold text-sm">
                                {member.fullname ? member.fullname[0].toUpperCase() : member.email[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-bold text-[#3D2542] flex items-center gap-2">
                                  {member.fullname || "Unnamed User"}
                                  {isCurrentUser && <span className="text-[9px] font-bold text-[#B89D4F] bg-[#FBF3DC] px-2 py-0.5 rounded-full">YOU</span>}
                                </div>
                                <div className="text-xs text-[#7A6B7E]">{member.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-5 whitespace-nowrap">
                            {canEditRole ? (
                              <select
                                value={member.role || "member"}
                                onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                                className="text-xs font-bold bg-white border border-[#B89D4F] rounded-lg px-3 py-1.5 text-[#5B3A60] focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/20 cursor-pointer shadow-sm"
                              >
                                <option value="member">Member</option>
                                <option value="admin">Admin</option>
                              </select>
                            ) : (
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide border ${
                                member.role === "admin" 
                                  ? "bg-[#FBF3DC] text-[#A88D3F] border-[#EEDFB0]" 
                                  : "bg-[#F0E6F5] text-[#5B3A60] border-[#E4DCE6]"
                              }`}>
                                {member.role === "admin" && <FaCrown size={10} />}
                                {member.role || "Member"}
                              </span>
                            )}
                          </td>
                          <td className="px-8 py-5 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${
                              member.is_active
                                ? "bg-[#E8F7F0] text-[#1F9D6C] border-[#BFE8D5]"
                                : "bg-[#FBF3DC] text-[#A88D3F] border-[#EEDFB0]"
                            }`}>
                              {member.is_active ? "Active" : "Pending"}
                            </span>
                          </td>
                          <td className="px-8 py-5 whitespace-nowrap text-sm text-[#7A6B7E]">
                            {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-8 py-5 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleDeleteMember(member.user_id, member.fullname)}
                              className="p-2 rounded-lg text-[#7A6B7E] hover:text-[#C62828] hover:bg-[#FDEEEE] transition-colors"
                            >
                              <FaTrash size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}